import { ZoneType, DensityType } from './types';

export interface CatalogBuilding {
  familyId: string;
  familyName: string;
  zone: ZoneType;
  density: DensityType;
  level: number;
  name: string;
  capacity: number;
  assetKey: string;
  description: string;
}

export const BuildingCatalog: Record<string, CatalogBuilding> = {};

// 1. RESIDENTIAL FAMILIES (A: Suburban, B: Modern, C: Classic, D: Green, E: High-rise)
const resFamilies = [
  { id: 'res_suburban', name: 'Suburban Standard' },
  { id: 'res_modern', name: 'Modern Architecture' },
  { id: 'res_classic', name: 'Classic Brick' },
  { id: 'res_green', name: 'Eco-Green' },
  { id: 'res_highrise', name: 'High-Density Condos' }
];

const resLevels = [
  { lvl: 1, name: 'Cottage', cap: 4, desc: 'Una piccola casa unifamiliare.' },
  { lvl: 2, name: 'Expanded Villa', cap: 8, desc: 'Una villa ampliata con garage.' },
  { lvl: 3, name: 'Townhouse', cap: 16, desc: 'Un complesso di villette a schiera.' },
  { lvl: 4, name: 'Apartment Block', cap: 32, desc: 'Un condominio residenziale medio.' },
  { lvl: 5, name: 'Large Condo', cap: 64, desc: 'Un grande palazzo residenziale con servizi.' },
  { lvl: 6, name: 'Residential Tower', cap: 128, desc: 'Un maestoso grattacielo residenziale.' }
];

resFamilies.forEach(fam => {
  resLevels.forEach(lvl => {
    const key = `${fam.id}_lvl${lvl.lvl}`;
    BuildingCatalog[key] = {
      familyId: fam.id,
      familyName: fam.name,
      zone: 'residential',
      density: lvl.lvl <= 2 ? 'low' : (lvl.lvl <= 4 ? 'medium' : 'high'),
      level: lvl.lvl,
      name: `${fam.name} - ${lvl.name}`,
      capacity: lvl.cap,
      assetKey: `building_residential_${fam.id}_lvl${lvl.lvl}`,
      description: lvl.desc
    };
  });
});

// 2. COMMERCIAL FAMILIES (A: Retail, B: Food/Boutiques, C: Leisure, D: Malls, E: Towers)
const comFamilies = [
  { id: 'com_retail', name: 'Emporio Retail' },
  { id: 'com_food', name: 'Boutiques & Caffè' },
  { id: 'com_leisure', name: 'Intrattenimento' },
  { id: 'com_mall', name: 'Grandi Magazzini' },
  { id: 'com_tower', name: 'Centro Commerciale Alto' }
];

const comLevels = [
  { lvl: 1, name: 'Boutique / Kiosk', cap: 2, desc: 'Un piccolo chiosco o negozio di angolo.' },
  { lvl: 2, name: 'Diner / Cafe', cap: 6, desc: 'Un ristorante accogliente o bar locale.' },
  { lvl: 3, name: 'Commercial Store', cap: 12, desc: 'Un esercizio commerciale medio.' },
  { lvl: 4, name: 'Supermarket / Center', cap: 24, desc: 'Un supermercato fornito di servizi.' },
  { lvl: 5, name: 'Department Plaza', cap: 48, desc: 'Un grande centro commerciale a più livelli.' },
  { lvl: 6, name: 'Commercial Skyscraper', cap: 96, desc: 'Una torre commerciale monumentale.' }
];

comFamilies.forEach(fam => {
  comLevels.forEach(lvl => {
    const key = `${fam.id}_lvl${lvl.lvl}`;
    BuildingCatalog[key] = {
      familyId: fam.id,
      familyName: fam.name,
      zone: 'commercial',
      density: lvl.lvl <= 2 ? 'low' : (lvl.lvl <= 4 ? 'medium' : 'high'),
      level: lvl.lvl,
      name: `${fam.name} - ${lvl.name}`,
      capacity: lvl.cap,
      assetKey: `building_commercial_${fam.id}_lvl${lvl.lvl}`,
      description: lvl.desc
    };
  });
});

// 3. INDUSTRIAL FAMILIES (A: Workshop, B: Manufacturing, C: High-Tech, D: Logistics, E: Heavy-Industry)
const indFamilies = [
  { id: 'ind_workshop', name: 'Artigianato & Workshop' },
  { id: 'ind_manufacture', name: 'Manifattura Standard' },
  { id: 'ind_hitech', name: 'Tecnologico & Chimico' },
  { id: 'ind_logistics', name: 'Logistica & Depositi' },
  { id: 'ind_heavy', name: 'Industria Pesante' }
];

const indLevels = [
  { lvl: 1, name: 'Storage Yard', cap: 4, desc: 'Un deposito di materiali all\'aperto.' },
  { lvl: 2, name: 'Craft Workshop', cap: 10, desc: 'Un\'officina artigianale o riparazioni.' },
  { lvl: 3, name: 'Assembly Plant', cap: 20, desc: 'Uno stabilimento di assemblaggio medio.' },
  { lvl: 4, name: 'Automated Factory', cap: 40, desc: 'Una fabbrica completamente automatizzata.' },
  { lvl: 5, name: 'Logistics Depot', cap: 80, desc: 'Un grande centro logistico intermodale.' },
  { lvl: 6, name: 'Heavy Refinery', cap: 160, desc: 'Un imponente complesso di raffineria pesante.' }
];

indFamilies.forEach(fam => {
  indLevels.forEach(lvl => {
    const key = `${fam.id}_lvl${lvl.lvl}`;
    BuildingCatalog[key] = {
      familyId: fam.id,
      familyName: fam.name,
      zone: 'industrial',
      density: lvl.lvl <= 2 ? 'low' : (lvl.lvl <= 4 ? 'medium' : 'high'),
      level: lvl.lvl,
      name: `${fam.name} - ${lvl.name}`,
      capacity: lvl.cap,
      assetKey: `building_industrial_${fam.id}_lvl${lvl.lvl}`,
      description: lvl.desc
    };
  });
});
